"use client";

import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import ChipSerif from "@/components/global/ui/ChipSerif";
import bg from "@/public/event/bg-hero.jpg";

import leafs from "@/public/replay/leafs.svg";

import { PageReplaySchema } from "@/sanity/pages/PageReplayTemplate";
import Footer from "@/components/global/partials/Footer";
import Image from "next/image";
import Video from "../global/Video";
import React, { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import bannerBG from "@/public/replay/mt5-banner-bg.webp";
import chadStatue from "@/public/replay/chad-statue.png";
import lastBanner from "@/public/replay/last-banner.jpg";
import { CTAButton } from "@/shadcn/ui/button";
import logoWhite from "@/public/logo-white.svg";

export default function PageReplayJSX({ data }: { data: PageReplaySchema }) {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [activeColumn, setActiveColumn] = useState<1 | 2>(1); // 1 = column 2, 2 = column 3

  const sectionIds = useMemo(
    () =>
      [
        data?.kryptoSectionTitle,
        data?.mt5SectionTitle,
        data?.futuresSectionTitle,
        data?.mt52SectionTitle,
        data?.marktvergleichSectionTitle,
        data?.jetztStartenSectionTitle,
      ].filter(Boolean) as string[],
    [
      data?.kryptoSectionTitle,
      data?.mt5SectionTitle,
      data?.futuresSectionTitle,
      data?.mt52SectionTitle,
      data?.marktvergleichSectionTitle,
      data?.jetztStartenSectionTitle,
    ],
  );

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (!el) continue;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setActiveSection(id);
            }
          }
        },
        {
          rootMargin: "-30% 0px -60% 0px",
          threshold: 0,
        },
      );

      observer.observe(el);
      observers.push(observer);
    }

    return () => {
      for (const obs of observers) obs.disconnect();
    };
  }, [sectionIds]);

  return (
    <>
      <main className="bg-[#07171B]">
        {/* Hero Section */}
        <section className="relative p-[1rem] lg:px-[3rem] lg:pt-[3rem]">
          <div className="flex relative flex-col items-center w-full bg-transparent overflow-clip bg-blend-darken rounded-[1.75rem] pb-[4rem] px-[2rem]  lg:rounded-[4rem] lg:px-[3rem]">
            <header className="flex absolute top-[1rem] justify-between z-[99] w-[min(176.5rem,100%)] px-[2rem] lg:px-0">
              <Image
                src={logoWhite}
                alt=""
                quality={100}
                className="pointer-events-none"
              />

              <div className="hidden absolute top-1/2 left-1/2 items-center -translate-x-1/2 -translate-y-1/2 lg:flex gap-[1rem]">
                {data?.headerAnchors.map((item, index) => (
                  <button
                    type="button"
                    key={index}
                    className="transition-all duration-200 cursor-pointer px-[1.5rem] py-[1rem] rounded-[1rem] hover:bg-white/20"
                    onClick={() => {
                      const target = document.querySelector(
                        `[data-body-id="${item.text}"]`,
                      );
                      target?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    <span className="m-0 text-white/70 text-[1.75rem] leading-[100%]">
                      {item.text}
                    </span>
                  </button>
                ))}
              </div>

              <div className="hidden lg:block">
                {data?.headerCta && (
                  <CTAButton data={data.headerCta} className="!w-fit" />
                )}
              </div>

              <div className="flex items-center rounded-full lg:hidden bg-[#FF5F5F]/10 w-fit px-[1.5rem] py-[1rem] gap-[1rem]">
                <div className="rounded-full w-[0.875rem] h-[0.875rem] bg-[#FFBABA]" />
                <p className="m-0 text-[1.75rem] text-[#FFBABA]">
                  {data?.headerMobileText}
                </p>
              </div>
            </header>
            <div className="absolute bottom-0 w-full bg-gradient-to-b from-transparent opacity-20 h-[69.75rem] to-[#A3DEB2] z-[2]" />

            <Image
              src={bg}
              alt=""
              quality={100}
              className="pointer-events-none object-cover absolute w-full !h-full"
            />

            <div className="relative z-[9] pt-[12rem] lg:pt-[10rem]">
              {data?.heroChip && (
                <div className="lg:mx-auto rounded-full border px-[1.5rem] w-fit border-white/30 lg:mb-[4rem] mb-[3rem]">
                  <ChipSerif
                    text={data.heroChip}
                    className="m-0 uppercase text-[1.75rem] leading-[150%] tracking-[.56px] text-white/60 font-geist"
                  />
                </div>
              )}

              {data?.heroText && (
                <div className="richtext richtext-start lg:richtext-center w-[min(98rem,100%)] mb-[2rem] prose-h1:text-white prose-p:text-white/70 lg:prose-h1:!text-[7.5rem] prose-h1:leading-[86%] prose-h1:!text-[6rem] lg:prose-p:text-[2rem] prose-p:text-[2.25rem] [&_br]:prose-p:hidden lg:[&_br]:prose-p:block prose-strong:font-[400] prose-strong:text-white prose-strong:pr-[.5rem] lg:prose-strong:pr-0 lg:mx-auto prose-p:w-[min(59.625rem,100%)] [&_span]:text-[#A3DEB2] prose-h1:mb-[3rem] [&_span]:font-[600] prose-h1:font-[500]">
                  <PortableText
                    value={data.heroText}
                    components={richTextComponent}
                  />
                </div>
              )}

              <div className="hidden items-center mx-auto lg:flex gap-[2rem] w-fit mb-[3rem] lg:mb-[4rem]">
                {data?.heroCta1 && (
                  <CTAButton
                    data={data.heroCta1}
                    className="[&_.button-text]:!text-white [&_.button-outline]:border-white/60 backdrop-blur-[2rem]"
                  />
                )}

                {data?.heroCta2 && <CTAButton data={data.heroCta2} />}
              </div>

              <div className=" relative [&>div]:h-full [&_video]:!object-cover aspect-video w-[min(94.875rem,100%)] mx-auto p-[.5rem] bg-[#07171B]/50 rounded-[2rem] backdrop-blur-[2rem] lg:p-[1rem]">
                <div className="w-full h-full">
                  <Video videoData={data?.heroVideo} />
                </div>
              </div>

              <div className="flex flex-col-reverse items-center mx-auto w-full lg:hidden gap-[1rem] lg:gap-[2rem] mt-[3rem]">
                {data?.heroCta1 && (
                  <CTAButton
                    data={data.heroCta1}
                    className="[&_.button-text]:!text-white [&_.button-outline]:border-white/60 backdrop-blur-[2rem]"
                  />
                )}

                {data?.heroCta2 && <CTAButton data={data.heroCta2} />}
              </div>
            </div>
          </div>
        </section>

        {/* Prizes Section */}
        <section className="flex lg:px-0 py-[7rem] lg:py-[8rem] px-[2rem]">
          <div className="flex flex-col lg:flex-row lg:flex-wrap w-[min(146.5rem,100%)] lg:gap-x-[15rem] items-center justify-center mx-auto gap-y-[3rem] lg:gap-y-auto">
            {data?.prizesItems &&
              data?.prizesItems.map((item, index) => (
                <div className="flex items-end lg:items-center gap-[1rem] lg:gap-[1.75rem] w-[min(30.5rem,100%)] lg:w-auto justify-between lg:justify-normal">
                  <Image src={leafs} alt="" className="lg:w-auto w-[2.75rem]" />
                  <div className="flex flex-col gap-[.25rem]">
                    <p className="tracking-[-.78px] leading-[112%] font-[600] lg:text-[3.25rem] text-white text-center m-0 text-[3rem]">
                      {item.value}
                    </p>
                    <div className="prose-p:text-white/70 prose-p:font-geist prose-p:text-center prose-p:font-[400] prose-p:leading-[120%] lg:prose-p:text-[2rem] text-[1.75rem] prose-p:mb-0 [&_br]:prose-p:hidden lg:[&_br]:prose-p:block">
                      <PortableText
                        value={item.text}
                        components={richTextComponent}
                      />
                    </div>
                  </div>
                  <Image
                    src={leafs}
                    alt=""
                    className="lg:w-auto -scale-x-100 w-[2.75rem]"
                  />
                </div>
              ))}
          </div>
        </section>

        {/* Rules Section */}
        <section className="lg:px-0">
          <div className="w-[min(176.5rem,100%)] mx-auto flex justify-between relative">
            <div className="hidden sticky flex-col h-full lg:flex top-[30rem] gap-[1.5rem]">
              {sectionIds.map((id, index) => (
                <div
                  key={id}
                  className={`border-b pb-[1.5rem] ${activeSection === id ? "border-[#A3DEB2]" : "border-white/20"}`}
                >
                  <p
                    className={`m-0 transition-colors duration-300 ${activeSection === id ? "text-white" : "text-white/30"}`}
                  >
                    {String(index + 1).padStart(2, "0")} {id}
                  </p>
                </div>
              ))}
            </div>

            <div className="w-[min(146.375rem,100%)] flex flex-col lg:gap-[7rem]">
              <div
                className="bg-[#85D0A6] lg:rounded-[2rem] p-[2rem] py-[7rem] lg:p-[7rem]"
                id={data?.kryptoSectionTitle}
                data-body-id={"Krypto"}
              >
                <div className="rounded-full border px-[1.5rem] py-[1rem] border-[#103037]/20 w-fit mb-[3rem]">
                  <p className="text-[#103037] font-instrument text-[2rem] leading-[100%] m-0">
                    {data?.kryptoChip}
                  </p>
                </div>

                <div>
                  <div className="w-[min(66.875rem,100%)] prose-h2:!text-[#103037] lg:prose-h2:text-[6rem] prose-h2:text-[4rem] prose-h2:font-[600] prose-h2:!tracking-[-1.44px] prose-h2:opacity-100 [&_span]:text-[#103037] prose-p:w-[min(56.875rem,100%)] prose-p:opacity-70 prose-p:text-[#103037] prose-h2:mb-[3rem] prose-p:m-0">
                    <PortableText
                      value={data.kryptoText}
                      components={richTextComponent}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-[1rem] mt-[3rem] lg:mt-[5rem]">
                  {data?.kryptoItems &&
                    data?.kryptoItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-col justify-between lg:flex-row lg:gap-0 bg-[#103037]/5 rounded-[1rem] p-[3rem] gap-[1rem]"
                      >
                        <div className="w-[min(44.5rem,100%)] shrink-0 flex flex-col gap-[4rem]">
                          <div className="flex flex-col py-[1rem] px-[1.5rem] bg-[#103037] rounded-[1.25rem] w-fit">
                            <p className="m-0 text-[#85D0A6] leading-[100%]">
                              0{index + 1}
                            </p>
                          </div>
                          <div className="richtext richtext-start prose-h3:text-[#103037] prose-h3:!font-[600] prose-h3:m-0 prose-h3:tracking-[-.72px] prose-h3:capitalize prose-h3:leading-[106%] prose-h3:text-[3rem] prose-h3:mb-[1rem]">
                            <PortableText
                              value={item.title}
                              components={richTextComponent}
                            />
                          </div>
                        </div>

                        <div className="richtext richtext-start prose-p:text-[2rem] w-[min(62.5rem,100%)]">
                          <PortableText
                            value={item.content}
                            components={richTextComponent}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div
                className="bg-[#103037] p-[2rem] py-[7rem] lg:rounded-[2rem] lg:p-[7rem]"
                id={data?.mt5SectionTitle}
                data-body-id={"MT5"}
              >
                <div className="rounded-full border px-[1.5rem] py-[1rem] border-white/20 w-fit mb-[2rem] lg:mb-[3rem]">
                  <p className="text-white font-instrument text-[2rem] leading-[100%] m-0">
                    {data?.mt5Chip}
                  </p>
                </div>

                <div className="flex flex-col gap-[2rem] lg:gap-[3rem] mb-[3rem]">
                  <div className="w-full prose-h2:!text-white lg:prose-h2:text-[6rem] prose-h2:text-[4rem] prose-h2:font-[600] prose-h2:!tracking-[-1.44px] prose-h2:opacity-100 [&_span]:text-[#A3DEB2] prose-p:opacity-70 prose-p:text-white prose-h2:mb-[3rem] prose-p:m-0">
                    <PortableText
                      value={data.mt5Text}
                      components={richTextComponent}
                    />
                  </div>

                  <div className="flex flex-col gap-[2rem] lg:gap-0">
                    {data?.mt5Checks &&
                      data?.mt5Checks.map((item, index) => (
                        <div>
                          <p className="m-0 text-white/70">{item.text}</p>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="flex relative flex-col bg-[#FFF7E2] rounded-[1rem] p-[3rem] gap-[2rem] overflow-clip">
                  <Image
                    src={bannerBG}
                    alt=""
                    quality={100}
                    className="hidden object-cover absolute top-0 right-0 bottom-0 lg:block w-[100.625rem] z-[1]"
                  />
                  <div className="richtext richtext-start prose-h6:bg-[#103037] prose-h6:text-[2rem] prose-h6:m-0 prose-h6:text-white prose-h6:px-[1.5rem] prose-h6:py-[1rem] prose-h6:rounded-[1.25rem] prose-h6:leading-[100%] prose-h6:mb-[2rem] lg:prose-h6:mb-[4rem] prose-h3:text-[#103037] prose-h3:font-geist prose-h3:text-[3rem] prose-h3:font-[600] prose-h3:leading-[120%] prose-h3:tracking-[-.72px] prose-h3:capitalize prose-h3:m-0 prose-h3:mb-[1rem] relative z-[5]">
                    <PortableText
                      value={data?.mt5Banner.title}
                      components={richTextComponent}
                    />
                  </div>

                  <div className="bg-[#EEE6D1] rounded-[2rem] p-[2rem] lg:p-[3rem] w-[min(67.5rem,100%)] grid lg:grid-cols-2 gap-x-[2rem] gap-y-[3rem] relative z-[5] grid-cols-1">
                    {data?.mt5Banner.items &&
                      data?.mt5Banner.items.map((item, index) => (
                        <div className="richtext richtext-start prose-p:m-0">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      ))}
                  </div>

                  {data?.mt5Banner.cta && (
                    <div className="lg:w-fit">
                      <CTAButton data={data?.mt5Banner.cta} />
                    </div>
                  )}
                </div>
              </div>

              <div
                className="bg-[#103037] p-[2rem] lg:rounded-[2rem] lg:p-[7rem]"
                id={data?.futuresSectionTitle}
                data-body-id={"Futures"}
              >
                <div className="rounded-full border px-[1.5rem] py-[1rem] border-white/20 w-fit mb-[2rem] lg:mb-[3rem]">
                  <p className="text-white font-instrument text-[2rem] leading-[100%] m-0">
                    {data?.futuresChip}
                  </p>
                </div>
                <div className="lg:space-y-0 space-y-[4rem]">
                  <div className="w-full prose-h2:!text-white lg:prose-h2:text-[6rem] prose-h2:text-[4rem] prose-h2:font-[600] prose-h2:!tracking-[-1.44px] prose-h2:opacity-100 [&_span]:text-[#93BCDB] prose-p:opacity-70 prose-p:text-white prose-h2:mb-[2rem] lg:prose-h2:mb-[3rem] prose-p:m-0 prose-p:w-[min(88.625rem,100%)]">
                    <PortableText
                      value={data.futuresTitle}
                      components={richTextComponent}
                    />
                  </div>

                  <div className="flex flex-col gap-[1.5rem]">
                    {data?.futuresItems &&
                      data?.futuresItems.map((item, index) => (
                        <div className="bg-[#93BCDB] p-[1rem] rounded-[1rem]">
                          <div className="w-full h-full py-[2rem] lg:py-[3rem] px-[1.5rem]">
                            <p className="text-[#103037] font-geist text-[3rem] font-[600] leading-[120%] tracking-[-.72px] capitalize m-0 mb-[1rem]">
                              {item.title}
                            </p>

                            <div className="richtext richtext-start prose-p:m-0">
                              <PortableText
                                value={item.text}
                                components={richTextComponent}
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                    {data?.futuresLastItem && (
                      <div className="bg-[#93BCDB] p-[1rem] rounded-[1rem]">
                        <div className="flex flex-col w-full h-full py-[2rem] lg:py-[3rem] px-[1.5rem] gap-[2rem]">
                          <div className="richtext richtext-start prose-h6:bg-[#103037] prose-h6:text-[2rem] prose-h6:m-0 prose-h6:text-white prose-h6:px-[1.5rem] prose-h6:py-[1rem] prose-h6:rounded-[1.25rem] prose-h6:leading-[100%] prose-h6:mb-[4rem] prose-h3:text-[#103037] prose-h3:font-geist prose-h3:text-[3rem] prose-h3:font-[600] prose-h3:leading-[120%] prose-h3:tracking-[-.72px] prose-h3:capitalize prose-h3:m-0 prose-h3:mb-[1rem]">
                            <PortableText
                              value={data?.futuresLastItem.title}
                              components={richTextComponent}
                            />
                          </div>

                          <div className="bg-[#80AACA] rounded-[2rem] p-[3rem]">
                            {data?.futuresLastItem.items &&
                              data?.futuresLastItem.items.map((item, index) => (
                                <React.Fragment key={index}>
                                  <div className="flex flex-col gap-[1rem]">
                                    <p className="text-[#103037] font-geist m-0 text-[2rem] !font-[700] leading-[150%]">
                                      {item.title}
                                    </p>
                                    <p className="text-[#103037]/80 font-geist m-0 text-[2rem] !font-[400] leading-[150%]">
                                      {item.text}
                                    </p>
                                  </div>

                                  {index <
                                    data.futuresLastItem.items.length - 1 && (
                                      <div className="border-dashed border-t-[.125rem] my-[2rem] border-black/20" />
                                    )}
                                </React.Fragment>
                              ))}
                          </div>

                          {data?.futuresCta && (
                            <div className="lg:w-fit">
                              <CTAButton data={data?.futuresCta} />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="bg-[#103037] p-[2rem] lg:rounded-[2rem] lg:p-[7rem]"
                id={data?.mt52SectionTitle}
                data-body-id={"MT52"}
              >
                <div className="rounded-full border px-[1.5rem] py-[1rem] border-white/20 w-fit mb-[2rem] lg:mb-[3rem]">
                  <p className="text-white font-instrument text-[2rem] leading-[100%] m-0">
                    {data?.mt52Chip}
                  </p>
                </div>

                <div className="flex flex-col gap-[2rem] lg:gap-[3rem] mb-[3rem]">
                  <div className="w-full prose-h2:!text-white lg:prose-h2:text-[6rem] prose-h2:text-[4rem] prose-h2:font-[600] prose-h2:!tracking-[-1.44px] prose-h2:opacity-100 [&_span]:text-[#A3DEB2] prose-p:opacity-70 prose-p:text-white prose-h2:mb-[2rem] lg:prose-h2:mb-[3rem] prose-p:m-0">
                    <PortableText
                      value={data.mt52Text}
                      components={richTextComponent}
                    />
                  </div>

                  <div className="flex flex-col gap-[2rem] lg:gap-0">
                    {data?.mt52Checks &&
                      data?.mt52Checks.map((item, index) => (
                        <div>
                          <p className="m-0 text-white/70">{item.text}</p>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="flex relative flex-col bg-[#FFF7E2] rounded-[1rem] p-[3rem] gap-[2rem] overflow-clip">
                  <Image
                    src={bannerBG}
                    alt=""
                    quality={100}
                    className="hidden object-cover absolute top-0 right-0 bottom-0 lg:block w-[100.625rem] z-[1]"
                  />
                  <div className="richtext richtext-start prose-h6:bg-[#103037] prose-h6:text-[2rem] prose-h6:m-0 prose-h6:text-white prose-h6:px-[1.5rem] prose-h6:py-[1rem] prose-h6:rounded-[1.25rem] prose-h6:leading-[100%] prose-h6:mb-[4rem] prose-h3:text-[#103037] prose-h3:font-geist prose-h3:text-[3rem] prose-h3:font-[600] prose-h3:leading-[120%] prose-h3:tracking-[-.72px] prose-h3:capitalize prose-h3:m-0 prose-h3:mb-[1rem] relative z-[5]">
                    <PortableText
                      value={data?.mt52Banner.title}
                      components={richTextComponent}
                    />
                  </div>

                  <div className="bg-[#EEE6D1] rounded-[2rem] p-[2rem] lg:p-[3rem] w-[min(67.5rem,100%)] grid lg:grid-cols-2 gap-x-[2rem] gap-y-[3rem] relative z-[5] grid-cols-1">
                    {data?.mt52Banner.items &&
                      data?.mt52Banner.items.map((item, index) => (
                        <div className="richtext richtext-start prose-p:m-0">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      ))}
                  </div>

                  {data?.mt52Banner.cta && (
                    <div className="lg:w-fit">
                      <CTAButton data={data?.mt52Banner.cta} />
                    </div>
                  )}
                </div>
              </div>

              <div
                className="bg-[#FFF7E2] p-[2rem] py-[7rem] lg:rounded-[2rem] lg:p-[7rem]"
                id={data?.marktvergleichSectionTitle}
                data-body-id={"Vergleich"}
              >
                <div className="rounded-full border px-[1.5rem] py-[1rem] border-[#103037]/20 w-fit mb-[2rem] lg:mb-[3rem]">
                  <p className="text-[#103037] font-instrument text-[2rem] leading-[100%] m-0">
                    {data?.marktvergleichChip}
                  </p>
                </div>

                <div className="flex flex-col gap-[2rem] lg:gap-[3rem] mb-[3rem]">
                  <div className="richtext richtext-start prose-h2:!text-[#103037] w-[min(84.5rem,100%)] lg:prose-h2:text-[6rem] prose-h2:text-[4rem] prose-h2:font-[600] prose-h2:!tracking-[-1.44px] prose-h2:opacity-100 [&_span]:text-[#103037] prose-h2:mb-[2rem] lg:prose-h2:mb-[3rem] prose-p:m-0">
                    <PortableText
                      value={data.marktvergleichTitle}
                      components={richTextComponent}
                    />
                  </div>
                </div>

                {/* Mobile Column Toggle Buttons */}
                {data?.marktvergleichTable && data.marktvergleichTable.length >= 3 && (
                  <div className="flex gap-[1rem] mb-[3rem] lg:hidden">
                    <button
                      type="button"
                      onClick={() => setActiveColumn(2)}
                      className={cn(
                        "py-[1.5rem] px-[2rem] rounded-[2rem] text-[2rem] font-[500] transition-all duration-200 text-black",
                        activeColumn === 2
                          ? "bg-[#E2D8C0]"
                          : "bg-[#E2D8C0]/30"
                      )}
                    >
                      {data.marktvergleichTable[2].columnTitle}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveColumn(1)}
                      className={cn(
                        "py-[1.5rem] px-[2rem] rounded-[2rem] text-[2rem] font-[500] transition-all duration-200 text-black",
                        activeColumn === 1
                          ? "bg-[#E2D8C0]"
                          : "bg-[#E2D8C0]/30"
                      )}
                    >
                      {data.marktvergleichTable[1].columnTitle}
                    </button>
                  </div>
                )}

                <div className="relative">
                  {data?.marktvergleichTable &&
                    (() => {
                      const allColumns = data.marktvergleichTable;
                      // Filter columns for mobile: show column 0 and selected column
                      const mobileColumns = [allColumns[0], allColumns[activeColumn]];
                      const maxRows = Math.max(
                        ...allColumns.map((col) => col.items.length),
                      );

                      return (
                        <>
                          {/* Mobile Grid: 2 columns */}
                          <div className="grid grid-cols-2 w-full gap-x-[1.5rem] z-[1] lg:hidden">
                            {/* Header row */}
                            {mobileColumns.map((col, colIndex) => (
                              <div
                                key={`header-mobile-${colIndex}`}
                                className={cn(
                                  "py-[2.25rem] rounded-t-[1.25rem]",
                                  colIndex === 0
                                    ? "bg-transparent"
                                    : "bg-[#E2D8C0]"
                                )}
                              >
                                <h5 className="m-0 text-center text-black text-[2.75rem] font-[500]">
                                  {col.columnTitle}
                                </h5>
                              </div>
                            ))}

                            {/* Cell rows */}
                            {Array.from({ length: maxRows }, (_, rowIndex) =>
                              mobileColumns.map((col, colIndex) => (
                                <div
                                  key={`cell-mobile-${rowIndex}-${colIndex}`}
                                  className={cn(
                                    "py-[2rem] px-0 lg:px-[3rem] flex items-center",
                                    colIndex === 0
                                      ? "bg-transparent relative"
                                      : "bg-[#EEE6D1]",
                                    rowIndex === maxRows - 1 &&
                                    colIndex === 1 &&
                                    "rounded-b-[1.25rem]"
                                  )}
                                >
                                  <p className="mx-auto lg:mx-0 mb-0 text-[2rem] lg:text-[2.25rem] text-center lg:text-start leading-[150%]">
                                    {col.items[rowIndex]?.text ?? ""}
                                  </p>
                                  {colIndex === 0 && rowIndex < maxRows - 1 && (
                                    <div
                                      className="absolute bottom-0 left-0 w-[calc(200%+1.5rem)] h-[.125rem] z-[5] pointer-events-none"
                                      style={{
                                        backgroundImage:
                                          "repeating-linear-gradient(to right, rgba(206,206,206,0.7) 0, rgba(206,206,206,0.7) 8px, transparent 8px, transparent 16px)",
                                      }}
                                    />
                                  )}
                                </div>
                              ))
                            )}
                          </div>

                          {/* Desktop Grid: 3 columns */}
                          <div className="hidden lg:grid grid-cols-3 w-full gap-x-[1.5rem] z-[1]">
                            {/* Header row */}
                            {allColumns.map((col, colIndex) => (
                            <div
                              key={`header-${colIndex}`}
                              className={cn(
                                "py-[2.25rem] rounded-t-[1.25rem] ",
                                colIndex === 0
                                  ? "bg-transparent"
                                  : colIndex === 1
                                    ? "bg-[#E2D8C0]/30"
                                    : "bg-[#E2D8C0]",
                              )}
                            >
                              <h5 className="m-0 text-center text-black text-[2.75rem] font-[500]">
                                {col.columnTitle}
                              </h5>
                            </div>
                          ))}

                          {/* Cell rows */}
                            {Array.from({ length: maxRows }, (_, rowIndex) =>
                              allColumns.map((col, colIndex) => (
                                <div
                                  key={`cell-${rowIndex}-${colIndex}`}
                                  className={cn(
                                    "py-[2rem] lg:px-[3rem] flex items-center",
                                    colIndex === 0
                                      ? "bg-transparent relative"
                                      : colIndex === 1
                                        ? "bg-[#EEE6D1]/30"
                                        : "bg-[#EEE6D1]",
                                    rowIndex === maxRows - 1 &&
                                    colIndex === 1 &&
                                    "rounded-b-[1.25rem]",
                                    rowIndex === maxRows - 1 &&
                                    colIndex === 2 &&
                                    "rounded-b-[1.25rem]",
                                  )}
                                >
                                  <p className="m-0 text-[2rem] lg:text-[2.25rem] text-center lg:text-start leading-[150%]">
                                    {col.items[rowIndex]?.text ?? ""}
                                  </p>
                                  {colIndex === 0 && rowIndex < maxRows - 1 && (
                                    <div
                                      className="absolute bottom-0 left-0 w-[calc(300%+3rem)] h-[.125rem] z-[5] pointer-events-none"
                                      style={{
                                        backgroundImage:
                                          "repeating-linear-gradient(to right, rgba(206,206,206,0.7) 0, rgba(206,206,206,0.7) 8px, transparent 8px, transparent 16px)",
                                      }}
                                    />
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </>
                      );
                    })()}
                </div>
              </div>

              <div
                className="relative bg-[#85D0A6] p-[2rem] pt-[7rem] lg:rounded-[2rem] overflow-clip lg:p-[7rem]"
                id={data?.jetztStartenSectionTitle}
                data-body-id={"Jetzt starten"}
              >
                <Image
                  src={lastBanner}
                  alt=""
                  className="hidden object-cover absolute inset-0 w-full h-full lg:block z-[1]"
                />
                <div className="relative rounded-full border px-[1.5rem] py-[1rem] border-[#103037]/30 w-fit mb-[2rem] lg:mb-[3rem] z-[5]">
                  <p className="text-[#103037] font-instrument text-[2rem] leading-[100%] m-0">
                    {data?.jetztStartenChip}
                  </p>
                </div>

                <div className="flex relative flex-col gap-[3rem] z-[5]">
                  <div className="richtext richtext-start prose-h2:!text-[#103037] w-[min(84.5rem,100%)] lg:prose-h2:text-[6rem] prose-h2:text-[4rem] prose-h2:font-[600] prose-h2:!tracking-[-1.44px] prose-h2:opacity-100 [&_span]:text-[#103037] prose-h2:mb-[2rem] lg:prose-h2:mb-[3rem] prose-p:m-0 prose-p:w-[min(50.875rem,100%)] prose-p:opacity-70 prose-p:text-[#103037]">
                    <PortableText
                      value={data.jetztStartenText}
                      components={richTextComponent}
                    />
                  </div>

                  <div className="flex flex-col w-fit gap-[1rem]">
                    {data?.jetztStartenCta && (
                      <CTAButton
                        data={data.jetztStartenCta}
                        className="!w-full"
                      />
                    )}

                    {data?.jetztStartenCallout && (
                      <p className="m-0 text-center text-black/60 text-[1.75rem] lg:text-start lg:w-auto w-[60%] mx-auto lg:mx-0">
                        {data.jetztStartenCallout}
                      </p>
                    )}
                  </div>
                </div>

                <Image
                  src={chadStatue}
                  alt=""
                  className="object-cover w-full lg:hidden translate-y-[2rem]"
                />
              </div>
            </div>
          </div>
        </section>

        {data?.settings?.footer && (
          <Footer
            className="[&_*]:text-white [&_.riskdisclosure_*]:!text-white/50 !pt-[8rem] lg:!pt-[16rem]"
            data={data.settings.footer}
            slug={data?.settings?.slug?.current}
          />
        )}
      </main>
    </>
  );
}
